import React, { useEffect, useMemo, useState } from "react";
import { catalogApi } from "../../api/auctiongate.js";

/**
 * Mini auction-fee calculator. Mirrors the plugin's view/calculator logic at a high level:
 *   - pick auction + fee-level
 *   - enter bid
 *   - compute auction fee from the local auctionFee.json table (copart/iaai × fee1low/fee1high/fee2…)
 */
const FEE_LEVELS = [
  { value: "fee1low", label: "Low (online buyer)" },
  { value: "fee1high", label: "High (preferred)" },
  { value: "fee2", label: "Tier 2" },
];

export default function Calculator() {
  const [auction, setAuction] = useState("copart");
  const [level, setLevel] = useState("fee1high");
  const [bid, setBid] = useState("1000");
  const [fee, setFee] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    catalogApi.fee().then(
      (r) => {
        if (!cancelled) setFee(r);
      },
      (e) => {
        if (!cancelled) setError(e?.message || "Failed to load fee table");
      }
    );
    return () => {
      cancelled = true;
    };
  }, []);

  const computed = useMemo(() => {
    const amount = Number(bid);
    if (!Number.isFinite(amount) || amount <= 0 || !fee) return null;
    const table = fee?.[auction]?.[level];
    if (!Array.isArray(table)) return null;
    const row = table.find((r) => Number(r.point) >= amount);
    const feeAmount = row ? Number(row.price) : Number(table[table.length - 1]?.price || 0);
    return {
      bid: amount,
      feeAmount,
      total: amount + feeAmount,
      description: row?.description ?? "above last tier",
    };
  }, [bid, fee, auction, level]);

  return (
    <div className="card">
      <div className="card__title">
        Calculator
        <span className="badge">auctionFee.json</span>
      </div>
      {error && <div className="login-error">{error}</div>}
      {!fee && !error && (
        <div>
          <span className="loader" /> <span className="muted small">Loading fee table…</span>
        </div>
      )}
      {fee && (
        <>
          <div className="row" style={{ marginBottom: 12 }}>
            <select value={auction} onChange={(e) => setAuction(e.target.value)} style={selectStyle}>
              <option value="copart">Copart</option>
              <option value="iaai">IAAI</option>
            </select>
            <select value={level} onChange={(e) => setLevel(e.target.value)} style={selectStyle}>
              {FEE_LEVELS.map((l) => (
                <option key={l.value} value={l.value}>
                  {l.label}
                </option>
              ))}
            </select>
            <input
              type="number"
              value={bid}
              onChange={(e) => setBid(e.target.value)}
              placeholder="Bid (USD)"
              style={{ ...selectStyle, flex: 1, minWidth: 120 }}
            />
          </div>

          {computed ? (
            <dl className="kv">
              <dt>Bid</dt>
              <dd>${computed.bid}</dd>
              <dt>Auction fee</dt>
              <dd>${computed.feeAmount}</dd>
              <dt>Total (no shipping)</dt>
              <dd style={{ color: "var(--color-accent)", fontSize: 16 }}>${computed.total}</dd>
              <dt>Tier</dt>
              <dd className="muted">{computed.description}</dd>
            </dl>
          ) : (
            <div className="small muted">Enter a positive bid amount.</div>
          )}
        </>
      )}
    </div>
  );
}

const selectStyle = {
  padding: "8px 10px",
  border: "1px solid var(--color-border-input)",
  borderRadius: 8,
  background: "#fff",
  fontSize: 14,
};
